/**
 * Puts the hand-off's active case on screen when the hand-off changes, and only then, so a
 * workbench mounting again leaves the run on screen alone. Switching is a swap, not a reset:
 * the case being left is remembered as it stands and the one opened is restored if it has
 * run before. A case is also put away when its run finishes.
 */

import { useEffect, useEffectEvent, useRef } from 'react';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { chatMessagesAtom } from '@/stores/chat.store';
import { lastRunDetailsAtom, thinkingStepsAtom } from '@/stores/websocket.store';
import { runReviewsAtom } from '../review/review.store';
import type { ScenarioCase } from './scenario-case';
import {
  actedOnSessionAtom,
  caseOnScreenAtom,
  caseRunKey,
  caseRunsAtom,
  conversationIdAtom,
  rememberCaseRunAtom,
  type CaseRun,
  type SimulatorSession,
} from './simulator-session.store';

interface CaseOnScreenOptions {
  /** The hand-off this screen follows; null when it follows none. */
  session: SimulatorSession | null;
  /** True while a run is in flight; its end puts the case away. */
  isWaiting: boolean;
  /** Puts a case on screen fresh. */
  load: (scenarioCase: ScenarioCase) => void;
  /** Puts back a case that has run before. */
  restore: (run: CaseRun, scenarioCase: ScenarioCase) => void;
}

export function useCaseOnScreen({ session, isWaiting, load, restore }: CaseOnScreenOptions): void {
  const store = useStore();
  const caseRuns = useAtomValue(caseRunsAtom);
  const rememberCaseRun = useSetAtom(rememberCaseRunAtom);

  const putAway = (key: string): void =>
    rememberCaseRun(key, {
      messages: store.get(chatMessagesAtom),
      conversationId: store.get(conversationIdAtom),
      thinkingSteps: store.get(thinkingStepsAtom),
      runDetails: store.get(lastRunDetailsAtom),
      reviews: store.get(runReviewsAtom),
    });

  const putOnScreen = useEffectEvent((handOff: SimulatorSession) => {
    if (handOff.activeIndex === null) return;
    const scenarioCase = handOff.cases[handOff.activeIndex];
    if (!scenarioCase || store.get(actedOnSessionAtom) === handOff) return;
    store.set(actedOnSessionAtom, handOff);

    const key = caseRunKey(handOff.id, handOff.activeIndex);
    const leaving = store.get(caseOnScreenAtom);
    if (leaving === key) return;
    if (leaving) putAway(leaving);
    store.set(caseOnScreenAtom, key);

    const cached = caseRuns[key];
    if (cached) restore(cached, scenarioCase);
    else load(scenarioCase);
  });

  useEffect(() => {
    if (session) putOnScreen(session);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- effect events are stable; this plugin version predates useEffectEvent
  }, [session]);

  const putAwayOnScreen = useEffectEvent(() => {
    const key = store.get(caseOnScreenAtom);
    if (key) putAway(key);
  });

  const wasWaitingRef = useRef(isWaiting);
  useEffect(() => {
    const finished = wasWaitingRef.current && !isWaiting;
    wasWaitingRef.current = isWaiting;
    if (finished) putAwayOnScreen();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- effect events are stable; this plugin version predates useEffectEvent
  }, [isWaiting]);
}
